import React from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, X, Plus } from 'lucide-react';
import { Button, Card, CardContent, CardHeader, CardTitle, ConfirmDialog } from '../../components/ui';
import { usePermissions } from '../../shared/lib/hooks/use-auth';
import { useRemoveUserRole } from '../../shared/lib/hooks/use-users';
import type { User, Role } from '../../shared/types';

interface UserRolesDisplayProps {
  user: User;
  onAssignRole?: () => void;
  className?: string;
}

export const UserRolesDisplay: React.FC<UserRolesDisplayProps> = ({
  user,
  onAssignRole,
  className,
}) => {
  const { t } = useTranslation();
  const { canManageUsers } = usePermissions();
  const removeRoleMutation = useRemoveUserRole();

  const [roleToRemove, setRoleToRemove] = React.useState<Role | null>(null);

  const handleRemoveRole = async () => {
    if (!roleToRemove) return;

    try {
      await removeRoleMutation.mutateAsync({
        userId: user.id,
        roleId: roleToRemove.id,
        serviceName: roleToRemove.serviceName,
      });
      setRoleToRemove(null);
    } catch (error) {
      console.error('Failed to remove role:', error);
    }
  };

  const formatRoleDisplay = (role: Role) => {
    if (role.serviceName) {
      return `${role.name} (${role.serviceName})`;
    }
    return role.name;
  };

  const getRoleDescription = (role: Role) => {
    const parts = [];
    if (role.description) {
      parts.push(role.description);
    }
    if (role.permissions && role.permissions.length > 0) {
      parts.push(`${role.permissions.length} permissions`);
    }
    return parts.join(' • ');
  };

  return (
    <>
      <Card className={className}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Shield className="h-4 w-4" />
              {t('users.roles')} ({user.roles.length})
            </CardTitle>
            {canManageUsers && (
              <Button
                variant="outline"
                size="sm"
                onClick={onAssignRole}
                className="h-8"
              >
                <Plus className="h-3 w-3 mr-1" />
                {t('users.assignRole')}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {user.roles.length === 0 ? (
            <div className="text-center py-6">
              <Shield className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground mb-3">
                {t('users.noRolesAssigned', 'No roles assigned to this user')}
              </p>
              {canManageUsers && (
                <Button variant="outline" size="sm" onClick={onAssignRole}>
                  <Plus className="h-3 w-3 mr-1" />
                  {t('users.assignFirstRole', 'Assign first role')}
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {user.roles.map((role) => (
                <div
                  key={`${role.id}-${role.serviceName || 'global'}`}
                  className="flex items-center justify-between p-3 border rounded-lg bg-muted/30"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">
                        {formatRoleDisplay(role)}
                      </span>
                      {role.serviceName && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-primary/10 text-primary">
                          {role.serviceName}
                        </span>
                      )}
                    </div>
                    {getRoleDescription(role) && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {getRoleDescription(role)}
                      </p>
                    )}
                  </div>
                  {canManageUsers && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setRoleToRemove(role)}
                      className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                      <span className="sr-only">
                        {t('users.removeRole', 'Remove role')}
                      </span>
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Remove role confirmation dialog */}
      <ConfirmDialog
        open={!!roleToRemove}
        onOpenChange={(open) => !open && setRoleToRemove(null)}
        title={t('users.removeRoleConfirmTitle', 'Remove Role')}
        description={
          roleToRemove
            ? t(
                'users.removeRoleConfirmDescription',
                `Are you sure you want to remove the role "${formatRoleDisplay(roleToRemove)}" from ${user.email}? This action cannot be undone.`
              )
            : ''
        }
        confirmText={t('common.remove', 'Remove')}
        cancelText={t('common.cancel')}
        variant="destructive"
        onConfirm={handleRemoveRole}
        loading={removeRoleMutation.isPending}
      />
    </>
  );
};